import React from 'react';
import { motion } from 'framer-motion';
import { CalendarCheckIcon, CheckCircle2Icon, HeartIcon, LeafIcon, TrendingUpIcon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const pillars = [{
  icon: LeafIcon,
  title: 'الاستدامة',
  description: 'تموّل المقاعد المدفوعة تقديم برامجنا يومًا بيوم، بما يضمن الجودة والاستمرارية لكل مجموعة.',
  tone: 'sage' as const
}, {
  icon: HeartIcon,
  title: 'الرسالة الاجتماعية',
  description: 'تفتح المنح الدراسية الباب للأسر التي تحتاج إلى الدعم، من خلال معايير شفافة ومقاعد مدعومة من المانحين.',
  tone: 'rose' as const
}, {
  icon: TrendingUpIcon,
  title: 'التوسّع',
  description: 'تتيح لنا المنح المؤسسية والشراكات الوصول إلى مزيد من الأسر في مناطق أكثر مع مرور الوقت.',
  tone: 'sand' as const
}];
const tuitionRows = [{
  age: '5–7',
  format: 'حصتان أسبوعيًا',
  price: '€90'
}, {
  age: '8–10',
  format: 'حصتان أسبوعيًا (مع حصة اختيارية مجانية إضافية)',
  price: '€120'
}, {
  age: '11–14',
  format: '3 حصص أسبوعيًا',
  price: '€150'
}, {
  age: '15–18',
  format: '3 حصص أسبوعيًا',
  price: '€180'
}];
const enrichmentRows = [{
  name: 'لغة إضافية',
  price: '€25–40 / شهريًا'
}, {
  name: 'الخط العربي',
  price: '€20–30 / شهريًا'
}, {
  name: 'الفنون والحِرف الإسلامية',
  price: '€30–50 / شهريًا'
}];
const familyPackages = [{
  icon: UsersIcon,
  eyebrow: 'خصم للأشقاء',
  value: '−15%',
  label: 'تسجيل طفلين',
  note: 'يُطبّق تلقائيًا على الرسوم الدراسية الشهرية.',
  tone: 'sage' as const
}, {
  icon: UsersRoundIcon,
  eyebrow: 'خصم للأشقاء',
  value: '−25%',
  label: 'تسجيل 3 أطفال أو أكثر',
  note: 'أفضل سعر نقدمه للأسر الكبيرة.',
  tone: 'rose' as const
}, {
  icon: CalendarCheckIcon,
  eyebrow: 'التزام سنوي',
  value: 'شهران مجانًا',
  label: 'ادفع مقابل 10 أشهر واحصل على 12',
  note: 'التزم للعام الدراسي ونمنحك شهرين مجانًا.',
  tone: 'sand' as const
}];
const supportTypes = [{
  title: 'منح جزئية',
  description: 'للأسر ذات الدخل المحدود أو التي تمر بصعوبات مالية مؤقتة.',
  tone: 'sage' as const
}, {
  title: 'منح كاملة',
  description: 'دعم ذو أولوية للأطفال الأكثر هشاشة، بما في ذلك حالات الإعاقة والأزمات.',
  tone: 'rose' as const
}, {
  title: 'مقاعد برعاية المجتمع',
  description: 'مقاعد ممولة من المانحين والشركاء، مع معايير وإجراءات إبلاغ واضحة.',
  tone: 'sand' as const
}];
const allocationPoints = ['تتقدم الأسر بطلب يتضمن تقييمًا مختصرًا وسريًا للاحتياج', 'تتبع القرارات معايير منشورة وواضحة', 'تُصان الكرامة؛ ولا تُعرّف أي أسرة علنًا', 'تُرفع تقارير بالنتائج كل ثلاثة أشهر إلى المانحين والشركاء'];

// Pricing section is temporarily hidden. Set to true to restore it.
const SHOW_PRICING_SECTION = false;
export function ArabicScholarships() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-3099" data-mp="xrlvh9yyap9k">
      <section className="relative overflow-hidden" data-id="element-3100" data-mp="swydzv2d6v4h">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-3101" data-mp="nzoe1lhcn12e" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-3102" data-mp="j14svnakxtb3" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-3103" data-mp="e0h3t75wvmji">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-8" data-id="element-3104" data-mp="9z5bgtvnkn7m">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col gap-6 lg:col-span-6" data-id="element-3105" data-mp="50kautcho1in">
              <span className="text-[15px] font-semibold text-forest-700" data-id="element-3106" data-mp="24bbuufypaab0">إتاحة الفرصة لكل طفل</span>
              <h1 className="font-display text-4xl font-semibold leading-[1.45] tracking-tight text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-3107" data-mp="w3o6m2cdpsy2">المنح الدراسية</h1>
              <div className="max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-3108" data-mp="111h84i2qlys0">
                <p data-id="element-3109" data-mp="n27xrz17pyvy">ينبغي أن يكون التعليم متاحًا وكريمًا ومستدامًا.</p>
                <p data-id="element-3110" data-mp="s1b1ng5ghz2c">تُدعم المنح الدراسية بالتبرعات والشراكات المؤسسية، وتُخصَّص بشفافية استنادًا إلى معايير واضحة.</p>
              </div>
              <div className="flex flex-wrap gap-3 pt-2" data-id="element-3111" data-mp="dbn2akgu5ah9">
                <Link to="/ar/waitlist" data-id="element-3112" data-mp="i4g6964hcesa"><Button variant="primary" size="lg" data-id="element-3113" data-mp="i4oicvss3g2f">التقدّم بطلب منحة</Button></Link>
                <Link to="/ar/children/kids-programs" data-id="element-3114" data-mp="4db1xqq62ybm"><Button variant="secondary" size="lg" data-id="element-3115" data-mp="4ecmclt2ox60">عرض البرامج</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-3116" data-mp="3qjq23whs0nrm">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-3117" data-mp="3bx1644mqgspz"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/8awkEnkAr35x3W2oQBLASf/Scholar-3.jpg" alt="طفلة مسلمة تدرس على حاسوب محمول في مساحة منزلية مضيئة" data-id="element-3118" data-mp="3bz3ukk88mdsl" /></div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="bg-sage-50/60 py-16 md:py-24" data-id="element-3119" data-mp="3gv2muiwosfya">
        <Container data-id="element-3120" data-mp="3gygmvkhvod2k">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3121" data-mp="3uvdeho5tlyki">نموذجنا الهجين</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-3122" data-mp="3pvr2tj5n4qdt">نتبع هيكلًا بسيطًا: المقاعد المدفوعة تصنع الاستدامة، والمنح الدراسية تحقق الأثر الاجتماعي، والمنح المؤسسية تتيح التوسّع.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-3123" data-mp="3gxx9vjvwy02">
            {pillars.map((pillar, index) => <PillarCard key={pillar.title} pillar={pillar} index={index} data-id="element-3124" data-mp="hgh0c9xu9j5i" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      {/* Pricing — temporarily hidden, see SHOW_PRICING_SECTION */}
      {SHOW_PRICING_SECTION && <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-3125" data-mp="kjwr6srndf8a">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3126" data-mp="piq007vzoi24" />
        <Container data-id="element-3127" data-mp="ugrnd50g73je">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3128" data-mp="ug81xebl9x0m">الرسوم الدراسية</h2>
          <p className="mt-5 max-w-4xl leading-loose text-ink-700" data-id="element-3129" data-mp="z8jb77s7yv5u">رسوم واضحة ومناسبة للأسر، صُممت بحيث تحافظ المقاعد المدفوعة على استدامة البرنامج وتفتح المنح الدراسية المجال لمن يحتاج إليها.</p>

          <div className="mt-12" data-id="element-3130" data-mp="1874vb7dcai8q">
            <h3 className="font-display text-2xl font-semibold leading-loose text-ink-900 md:text-3xl" data-id="element-3131" data-mp="18a95qmheea7u">الرسوم الأساسية (شهريًا)</h3>
            <p className="mt-3 leading-loose text-ink-700" data-id="element-3132" data-mp="1i62dlsk69fja"><strong className="text-ink-900" data-id="element-3133" data-mp="1i5u141ryoc8m">يشمل:</strong> التعلّم الأساسي / الأساس الإسلامي والثقافي / إثراء أساسي (حصة واحدة)</p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-sage-200 bg-white shadow-soft" data-id="element-3134" data-mp="3f0yv5pmrld3b">
              <div className="grid grid-cols-[0.8fr_1.7fr_auto] gap-3 border-b border-sage-200 bg-sage-50 px-4 py-4 text-xs font-semibold text-ink-900 sm:gap-4 sm:px-6" data-id="element-3135" data-mp="212kryeo5u2uq"><span data-id="element-3136" data-mp="1wab557icdiwo">الفئة العمرية</span><span data-id="element-3137" data-mp="1w9rmtpos4si4">الصيغة</span><span data-id="element-3138" data-mp="1saagku1j3rbw">السعر</span></div>
              {tuitionRows.map((row, index) => <div key={row.age} className={`grid grid-cols-[0.8fr_1.7fr_auto] items-center gap-3 px-4 py-5 text-sm sm:gap-4 sm:px-6 ${index < tuitionRows.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3139" data-mp="1saisojptusm1" data-mpi={index}><span className="font-display text-lg font-semibold text-ink-900" data-id="element-3140" data-mp="2k0x3g72jnoth">{row.age}</span><span className="leading-loose text-ink-700" data-id="element-3141" data-mp="2g0wkqa8ewrgm">{row.format}</span><span dir="ltr" className="font-display text-lg font-semibold text-forest-700" data-id="element-3142" data-mp="2b0id2taq3jj4">{row.price}</span></div>)}
            </div>
          </div>

          <div className="mt-14" data-id="element-3143" data-mp="2615r0aroeghl">
            <h3 className="text-sm font-semibold text-ink-900" data-id="element-3144" data-mp="zllmvg2syoa6">دروس الإثراء (اختيارية)</h3>
            <div className="mt-5 overflow-hidden rounded-2xl border border-sage-200 bg-white shadow-soft" data-id="element-3145" data-mp="zj8wqfmw57m1">
              <div className="grid grid-cols-[1.5fr_1fr] gap-4 border-b border-sage-200 bg-sage-50 px-6 py-4 text-xs font-semibold text-ink-900" data-id="element-3146" data-mp="2uorqnfy0uejk"><span data-id="element-3147" data-mp="2zo4p6xy99a1z">الدورة</span><span data-id="element-3148" data-mp="2zomj3nxg3c6q">السعر</span></div>
              {enrichmentRows.map((row, index) => <div key={row.name} className={`grid grid-cols-[1.5fr_1fr] items-center gap-4 px-6 py-5 ${index < enrichmentRows.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3149" data-mp="2kr3tykguuwyr" data-mpi={index}><span className="font-medium text-ink-900" data-id="element-3150" data-mp="2prro3f0drtx7">{row.name}</span><span dir="ltr" className="font-display text-lg font-semibold text-forest-700" data-id="element-3151" data-mp="3dgawdujfk6pa">{row.price}</span></div>)}
            </div>
          </div>

          <div className="mt-14" data-id="element-3152" data-mp="33pg6mg6z6oz3">
            <h3 className="text-sm font-semibold text-ink-900" data-id="element-3153" data-mp="33q81tpwnukou">باقات الأسرة</h3>
            <p className="mt-3 leading-loose text-ink-700" data-id="element-3154" data-mp="38o9i632pc5wm">هل تسجل أكثر من طفل، أو تلتزم للعام الدراسي؟ نجعل ذلك أسهل بتخفيضات مدمجة.</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3155" data-mp="7y1igil7w2d">{familyPackages.map((item, index) => <PackageCard key={item.label} item={item} index={index} data-id="element-3156" data-mp="507wiayk7mbi" data-mpi={index} />)}</div>
            <p className="mt-5 flex items-center gap-2 text-sm leading-loose text-ink-500" data-id="element-3157" data-mp="1pp39broqe4w3"><CheckCircle2Icon size={16} className="shrink-0 text-forest-700" strokeWidth={1.8} aria-hidden="true" data-id="element-3158" data-mp="1kr1qn7v7w7l5" />يمكن الجمع بين الخصومات والمنح الدراسية عند استيفاء الشروط.</p>
          </div>
        </Container>
      </section>}

      <section className="relative isolate overflow-hidden bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-3159" data-mp="1uhwfmjzullaa">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-3160" data-mp="16s22e1bm9upi" />
        <Container data-id="element-3161" data-mp="11rw9khtm225u">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3162" data-mp="11sftg42tn0mi">أنواع الدعم</h2>
          <p className="mt-5 leading-loose text-ink-700" data-id="element-3163" data-mp="1fsqfmtt6rr1c">لا ينبغي أن يُستبعد أي طفل من برامجنا بسبب الظروف المالية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-3164" data-mp="1boitn4e44p6t">{supportTypes.map((item, index) => <SupportCard key={item.title} item={item} index={index} data-id="element-3165" data-mp="2r5j7s5pw2hhx" data-mpi={index} />)}</div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-3166" data-mp="2n4pj3a4mgynz">
        <Container data-id="element-3167" data-mp="1ozycq0ih2str">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-3168" data-mp="1oydh07kf93ub">
            <div className="lg:col-span-6" data-id="element-3169" data-mp="1ky3dd1k3qpim">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-3170" data-mp="1kz4rjewezs4k">كيف يتم التخصيص</h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3171" data-mp="1g50fwic7evx5">{allocationPoints.map(point => <li key={point} className="flex items-start gap-3 leading-loose" data-id="element-3172" data-mp="1b7ih3krwhx2v"><CheckCircle2Icon size={18} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-3173" data-mp="169gpuckbmrgb" /><span data-id="element-3174" data-mp="119krotyb1u8o">{point}</span></li>)}</ul>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-3175" data-mp="11byp39ggjije"><Link to="/ar/waitlist" data-id="element-3176" data-mp="x7qwh16vpifw"><Button variant="primary" data-id="element-3177" data-mp="x91zuzwewo6h">التقدّم بطلب</Button></Link><Link to="/ar/partnership/donate-for-children" data-id="element-3178" data-mp="s95wn2vjh0rz"><Button variant="secondary" data-id="element-3179" data-mp="sah9rtavjkvr">ادعم طفلًا</Button></Link></div>
            </div>
            <div className="lg:col-span-6" data-id="element-3180" data-mp="2rl8op1i8b3h9"><div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-card" data-id="element-3181" data-mp="2rlidilrjf4ld"><img src="https://cdn.magicpatterns.com/uploads/xvXUHwEJEt7AMzu9f6ER5q/Book.jpg" alt="طفل مسلم يكتب ملاحظات أثناء الدراسة" className="h-full w-full object-cover" data-id="element-3182" data-mp="2mo8imqldq0q5" /></div></div>
          </div>
        </Container>
      </section>
    </div>;
}
function PillarCard({
  pillar,
  index
}: {
  pillar: (typeof pillars)[number];
  index: number;
}) {
  const Icon = pillar.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[pillar.tone]}`} data-id="element-3183" data-mp="38w42a74no0zk"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-3184" data-mp="2g918fsaxcefn"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-3185" data-mp="2p7d9ko9f2umc" /></span><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3186" data-mp="2p8y3qcrtkbjo">{pillar.title}</h3><p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-3187" data-mp="fizx96j465f">{pillar.description}</p></motion.article>;
}
function PackageCard({
  item,
  index
}: {
  item: (typeof familyPackages)[number];
  index: number;
}) {
  const Icon = item.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[item.tone]}`} data-id="element-3188" data-mp="1wj7ceeel5qxp"><span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-forest-700/10 bg-white text-forest-700" data-id="element-3189" data-mp="25hj30gfgcoq5"><Icon size={22} strokeWidth={1.6} aria-hidden="true" data-id="element-3190" data-mp="1dszecapqtqod" /></span><p className="mt-5 text-xs font-semibold text-forest-700" data-id="element-3191" data-mp="1dqcvvg1bdqrd">{item.eyebrow}</p><p className="mt-2 font-display text-3xl font-semibold leading-none text-ink-900" data-id="element-3192" data-mp="1rkm8a3w7qn3z">{item.value}</p><h4 className="mt-4 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3193" data-mp="1msceudvw34wy">{item.label}</h4><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-3194" data-mp="z28k6w0oifi3">{item.note}</p></motion.article>;
}
function SupportCard({
  item,
  index
}: {
  item: (typeof supportTypes)[number];
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[item.tone]}`} data-id="element-3195" data-mp="35r0iv494tjdy"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3196" data-mp="31q8asebzxkj9">{item.title}</h3><p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-3197" data-mp="3pft5fuhii68d">{item.description}</p></motion.article>;
}